import { motion } from 'framer-motion';
import { Shield, Lock, Eye, FileText } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-full mb-6">
              <Shield className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-slate-400">
              Last Updated: February 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg prose-invert max-w-none"
          >
            <div className="bg-slate-800 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Lock className="w-6 h-6 text-emerald-400" />
                Our Commitment to Your Privacy
              </h2>
              <p className="text-slate-300">
                At Cornerstone Refund, we take your privacy seriously. This Privacy Policy explains how we 
                collect, use, disclose, and safeguard your information when you visit our website or use 
                our services. Please read this policy carefully.
              </p>
            </div>

            <div className="space-y-8 text-slate-300">
              <section>
                <h3 className="text-xl font-semibold text-white mb-4">1. Information We Collect</h3>
                <p className="mb-4">We may collect information about you in various ways, including:</p>
                
                <h4 className="text-lg font-medium text-white mb-2">Personal Information</h4>
                <p className="mb-4">
                  When you contact us or submit a form on our website, we may collect personally identifiable 
                  information such as:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-4">
                  <li>Your name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Mailing address</li>
                  <li>Property information relevant to your overage fund claim</li>
                </ul>

                <h4 className="text-lg font-medium text-white mb-2">Automatically Collected Information</h4>
                <p>
                  When you visit our website, we may automatically collect certain information about your 
                  device, including your IP address, browser type, operating system, and pages visited. This 
                  helps us improve our website and services.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">2. How We Use Your Information</h3>
                <p className="mb-4">We use the information we collect to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Process and manage your overage fund recovery claim</li>
                  <li>Communicate with you about your case status</li>
                  <li>Send you important updates and information about our services</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">3. Information Sharing</h3>
                <p className="mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share 
                  your information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong className="text-white">With your consent:</strong> When you authorize us to share information</li>
                  <li><strong className="text-white">For legal purposes:</strong> To comply with legal requirements or respond to lawful requests</li>
                  <li><strong className="text-white">Service providers:</strong> With trusted third parties who assist us in operating our business (subject to confidentiality agreements)</li>
                  <li><strong className="text-white">Claim processing:</strong> With relevant county offices and courts as necessary to process your overage fund claim</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">4. Data Security</h3>
                <p>
                  We implement appropriate technical and organizational measures to protect your personal 
                  information against unauthorized access, alteration, disclosure, or destruction. However, 
                  no method of transmission over the Internet is 100% secure, and we cannot guarantee 
                  absolute security.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">5. Your Rights</h3>
                <p className="mb-4">You have the right to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your information (subject to legal requirements)</li>
                  <li>Opt out of marketing communications</li>
                  <li>Lodge a complaint with a supervisory authority</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">6. Cookies and Tracking</h3>
                <p>
                  Our website may use cookies and similar tracking technologies to enhance your experience. 
                  You can control cookies through your browser settings. We also use Google Analytics to 
                  understand how visitors interact with our website.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">7. Third-Party Links</h3>
                <p>
                  Our website may contain links to third-party websites. We are not responsible for the 
                  privacy practices of these external sites. We encourage you to read the privacy policies 
                  of any website you visit.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">8. Children's Privacy</h3>
                <p>
                  Our services are not intended for individuals under the age of 18. We do not knowingly 
                  collect personal information from children.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">9. Changes to This Policy</h3>
                <p>
                  We may update this Privacy Policy from time to time. Any changes will be posted on this 
                  page with an updated "Last Updated" date. We encourage you to review this policy 
                  periodically.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">10. Contact Us</h3>
                <p className="mb-4">
                  If you have any questions about this Privacy Policy or our privacy practices, please 
                  contact us:
                </p>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="mb-2"><strong className="text-white">Cornerstone Refund</strong></p>
                  <p>2345 E. Thomas Rd Ste 100 #510</p>
                  <p>Phoenix, AZ 85016</p>
                  <p>Phone: <a href="tel:405-754-2183" className="text-emerald-400 hover:underline">405-754-2183</a></p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
